import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";

const reasons = [
  {
    icon: "✦",
    title: "Zero Learning Curve",
    desc: "Get your entire commercial team up and running in a day — no IT department or technical background required.",
  },
  {
    icon: "✦",
    title: "Enterprise-Grade Security",
    desc: "256-bit encryption, daily backups, and role-based access controls keep your data and industrial tenant records protected.",
  },
  {
    icon: "✦",
    title: "Scales With Your Portfolio",
    desc: "Whether you manage 5 bays or 500 commercial units, FMS handles the complexity so you don't have to.",
  },
  {
    icon: "✦",
    title: "Real Support from Real People",
    desc: "Our team understands commercial real estate. Get answers from people who know NNN leases, CAM charges, and flex industrial operations.",
  },
];

const cardVariants = {
  hidden: { opacity: 0, y: 40 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.55, ease: "easeOut", delay: i * 0.12 },
  }),
};

const FlexManagement = () => {
  return (
    <div className="lg:pb-44 xl:pb-60 2xl:pb-72 bg-white">
      <div className="bg-whyFlex bg-no-repeat bg-cover min-h-screen py-20 flex flex-col justify-center">
        <div className="w-[90%] lg:w-[75%] mx-auto">
          {/* Top heading */}
          <motion.div
            className="text-center mb-14"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.4 }}
            transition={{ duration: 0.65, ease: "easeOut" }}
          >
            <p className="font-popinsMedium text-sm uppercase tracking-widest text-white/70 mb-3">
              Why Operators Choose FMS
            </p>
            <h2 className="text-white font-popinsBold text-4xl md:text-5xl xl:text-6xl leading-tight">
              Why Flex <br className="hidden md:block" />
              Management System?
            </h2>
          </motion.div>

          {/* Reason grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-14">
            {reasons.map((r, i) => (
              <motion.div
                key={i}
                custom={i}
                variants={cardVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
                className="bg-white/10 hover:bg-white/15 border border-white/20 hover:border-white/40 rounded-2xl p-7 transition-all duration-300"
              >
                <span className="text-btnColor text-xl font-popinsBold">{r.icon}</span>
                <h3 className="font-popinsSemiBold text-white text-lg mt-3 mb-2">
                  {r.title}
                </h3>
                <p className="font-popinsRegular text-white/75 text-sm leading-relaxed">
                  {r.desc}
                </p>
              </motion.div>
            ))}
          </div>

          {/* CTA */}
          <motion.div
            className="flex justify-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.5 }}
            transition={{ duration: 0.5, ease: "easeOut" }}
          >
            <Link to="/why-flex">
              <button className="animated-button">
                <svg viewBox="0 0 24 24" className="arr-2" xmlns="http://www.w3.org/2000/svg">
                  <path d="M16.1716 10.9999L10.8076 5.63589L12.2218 4.22168L20 11.9999L12.2218 19.778L10.8076 18.3638L16.1716 12.9999H4V10.9999H16.1716Z" />
                </svg>
                <span className="text font-popinsSemiBold text-sm">Learn More About FMS</span>
                <span className="circle" />
                <svg viewBox="0 0 24 24" className="arr-1" xmlns="http://www.w3.org/2000/svg">
                  <path d="M16.1716 10.9999L10.8076 5.63589L12.2218 4.22168L20 11.9999L12.2218 19.778L10.8076 18.3638L16.1716 12.9999H4V10.9999H16.1716Z" />
                </svg>
              </button>
            </Link>
          </motion.div>
        </div>

        {/* Dashboard screenshot */}
        <motion.div
          className="w-[85%] mx-auto mt-10"
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
        >
          <img
            src="/assets/whyflexhomesection.png"
            alt="FMS Dashboard Preview"
            className="size-full object-contain drop-shadow-2xl"
          />
        </motion.div>
      </div>
    </div>
  );
};

export default FlexManagement;
